import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  LogOut,
  Trash2,
  AlertTriangle,
  Clock,
  Calendar,
  Shield,
  Loader2,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Separator } from '../../components/ui/separator';

import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [loadingAccountInfo, setLoadingAccountInfo] = useState(true);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    reset: resetEmail,
    formState: { errors: emailErrors },
  } = useForm();

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    watch: watchPassword,
    formState: { errors: passwordErrors },
  } = useForm();

  useEffect(() => {
    authAPI.getMe()
      .then((data) => setAccountInfo(data.user || data))
      .catch(() => setAccountInfo(user))
      .finally(() => setLoadingAccountInfo(false));
  }, [user]);

  const onChangeEmail = async (data) => {
    setIsChangingEmail(true);
    try {
      await authAPI.changeEmail(data.newEmail, data.emailPassword);
      toast.success('Email updated successfully!');
      resetEmail();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to change email');
    } finally {
      setIsChangingEmail(false);
    }
  };

  const onChangePassword = async (data) => {
    setIsChangingPassword(true);
    try {
      await authAPI.changePassword(data.currentPassword, data.newPassword, data.confirmPassword);
      toast.success('Password updated successfully!');
      resetPassword();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await authAPI.deactivateAccount();
      toast.success('Account deactivated');
      setShowDeactivateDialog(false);
      logout();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to deactivate account');
    }
  };

  const handleDelete = async () => {
    try {
      await authAPI.deleteAccount();
      toast.success('Account deleted');
      setShowDeleteDialog(false);
      logout();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete account');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 mb-4 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Account Settings</h1>
          <p className="text-slate-500 mt-1">Manage your account security and preferences</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Account Summary */}
          <motion.div variants={itemVariants}>
            <Card className="bg-white/80 border-slate-100 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-sky-600" />
                  Account Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingAccountInfo ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-sky-600" />
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-xl">
                        <Calendar className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Account Created</p>
                        <p className="text-sm font-semibold text-slate-800">{formatDate(accountInfo?.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 rounded-xl">
                        <Clock className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Last Login</p>
                        <p className="text-sm font-semibold text-slate-800">{formatDate(accountInfo?.lastLogin || new Date().toISOString())}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-xl">
                        <Mail className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Email Address</p>
                        <p className="text-sm font-semibold text-slate-800">{user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-xl ${accountInfo?.active !== false ? 'bg-green-100' : 'bg-red-100'}`}>
                        <Settings className={`w-4 h-4 ${accountInfo?.active !== false ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Account Status</p>
                        <p className="text-sm font-semibold text-slate-800">{accountInfo?.active !== false ? 'Active' : 'Inactive'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Change Email */}
          <motion.div variants={itemVariants}>
            <Card className="bg-white/80 border-slate-100 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Mail className="w-5 h-5 text-sky-600" />
                  Change Email Address
                </CardTitle>
                <CardDescription>Update your email address. You'll need to verify your password.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitEmail(onChangeEmail)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newEmail">New Email Address</Label>
                    <Input
                      id="newEmail"
                      type="email"
                      placeholder="new.email@example.com"
                      disabled={isChangingEmail}
                      {...registerEmail('newEmail', {
                        required: 'Email is required',
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                      })}
                    />
                    {emailErrors.newEmail && <p className="text-xs text-red-500">{emailErrors.newEmail.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emailPassword">Current Password</Label>
                    <Input
                      id="emailPassword"
                      type="password"
                      placeholder="Enter your current password"
                      disabled={isChangingEmail}
                      {...registerEmail('emailPassword', { required: 'Password is required' })}
                    />
                    {emailErrors.emailPassword && <p className="text-xs text-red-500">{emailErrors.emailPassword.message}</p>}
                  </div>
                  <Button
                    type="submit"
                    disabled={isChangingEmail}
                    className="bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 rounded-xl"
                  >
                    {isChangingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating...</> : 'Change Email'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Change Password */}
          <motion.div variants={itemVariants}>
            <Card className="bg-white/80 border-slate-100 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Lock className="w-5 h-5 text-sky-600" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your password to keep your account secure.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitPassword(onChangePassword)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        placeholder="Enter your current password"
                        disabled={isChangingPassword}
                        {...registerPassword('currentPassword', { required: 'Current password is required' })}
                      />
                      <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && <p className="text-xs text-red-500">{passwordErrors.currentPassword.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        disabled={isChangingPassword}
                        {...registerPassword('newPassword', { required: 'New password is required', minLength: { value: 8, message: 'At least 8 characters' } })}
                      />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.newPassword && <p className="text-xs text-red-500">{passwordErrors.newPassword.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your new password"
                        disabled={isChangingPassword}
                        {...registerPassword('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: (v) => v === watchPassword('newPassword') || 'Passwords do not match',
                        })}
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && <p className="text-xs text-red-500">{passwordErrors.confirmPassword.message}</p>}
                  </div>

                  <Button
                    type="submit"
                    disabled={isChangingPassword}
                    className="bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 rounded-xl"
                  >
                    {isChangingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating...</> : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Danger Zone */}
          <motion.div variants={itemVariants}>
            <Card className="bg-white/80 border-red-100 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription>Irreversible actions that affect your account.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-100 rounded-xl">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Logout</h4>
                    <p className="text-xs text-slate-500">Sign out of your account on this device</p>
                  </div>
                  <Button onClick={() => setShowLogoutDialog(true)} variant="outline" className="border-yellow-400 text-yellow-700 hover:bg-yellow-50 rounded-xl">
                    <LogOut className="w-4 h-4 mr-2" />Logout
                  </Button>
                </div>

                <Separator />

                <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-100 rounded-xl">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Deactivate Account</h4>
                    <p className="text-xs text-slate-500">Temporarily disable your account. You can reactivate it later.</p>
                  </div>
                  <Button onClick={() => setShowDeactivateDialog(true)} variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50 rounded-xl">
                    <Settings className="w-4 h-4 mr-2" />Deactivate
                  </Button>
                </div>

                <Separator />

                <div className="flex items-center justify-between p-4 bg-red-50 border border-red-100 rounded-xl">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Delete Account</h4>
                    <p className="text-xs text-slate-500">Permanently delete your account and all associated data.</p>
                  </div>
                  <Button onClick={() => setShowDeleteDialog(true)} variant="destructive" className="rounded-xl">
                    <Trash2 className="w-4 h-4 mr-2" />Delete Account
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>

      {/* Logout Confirmation */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Logout</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to log out of your account?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={logout} className="bg-yellow-600 hover:bg-yellow-700">Logout</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate Confirmation */}
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Account</AlertDialogTitle>
            <AlertDialogDescription>This will temporarily disable your account. You can reactivate it later by logging in again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-orange-600 hover:bg-orange-700">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              This action is <strong>permanent and irreversible</strong>. All your data will be deleted. Are you absolutely sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete Account</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
